import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AgentCard } from '../components/AgentCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Search, MapPin, Filter, Award, Loader2, Building, Map, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [agents, setAgents] = useState([]);
    const [nearbyAgents, setNearbyAgents] = useState([]);
    const [geoInfo, setGeoInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [specializations, setSpecializations] = useState([]);

    // Geo autocomplete
    const [searchInput, setSearchInput] = useState(searchParams.get('city') || '');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsRef = useRef(null);
    const debounceRef = useRef(null);

    const [filters, setFilters] = useState({
        city: searchParams.get('city') || '',
        department: searchParams.get('department') || '',
        region: searchParams.get('region') || '',
        specialization: searchParams.get('specialization') || '',
        topPerformersOnly: searchParams.get('top') === 'true',
    });

    useEffect(() => {
        fetchSpecializations();
        const handler = (e) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => { fetchAgents(); }, [filters]);

    const fetchSpecializations = async () => {
        try {
            const res = await axios.get(`${API}/stats/specializations`);
            setSpecializations(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchAgents = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.city) params.append('city', filters.city);
            if (filters.department) params.append('department', filters.department);
            if (filters.region) params.append('region', filters.region);
            if (filters.specialization) params.append('specialization', filters.specialization);
            if (filters.topPerformersOnly) params.append('top_performers_only', 'true');

            const response = await axios.get(`${API}/agents?${params.toString()}`);
            const data = response.data;
            setAgents(data.agents || []);
            setNearbyAgents(data.nearby_agents || []);
            setGeoInfo(data.geo_info || null);
        } catch (error) {
            console.error('Error fetching agents:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGeoSearch = useCallback(async (query) => {
        if (query.length < 2) { setSuggestions([]); return; }
        try {
            const res = await axios.get(`${API}/geo/search?q=${encodeURIComponent(query)}&limit=6`);
            setSuggestions(res.data);
            setShowSuggestions(true);
        } catch (e) { console.error(e); }
    }, []);

    const handleInputChange = (value) => {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => handleGeoSearch(value), 300);
    };

    const selectSuggestion = (suggestion) => {
        setShowSuggestions(false);
        if (suggestion.type === 'department') {
            setSearchInput(`${suggestion.name} (${suggestion.department_code})`);
            applyFilters({ city: '', department: suggestion.department_code, region: '' });
        } else {
            setSearchInput(suggestion.name);
            applyFilters({ city: suggestion.name, department: '', region: '' });
        }
    };

    const searchByDepartment = () => {
        if (geoInfo) {
            setSearchInput(`${geoInfo.department_name} (${geoInfo.department_code})`);
            applyFilters({ city: '', department: geoInfo.department_code, region: '' });
        }
    };

    const searchByRegion = () => {
        if (geoInfo) {
            setSearchInput(geoInfo.region_name);
            applyFilters({ city: '', department: '', region: geoInfo.region_name });
        }
    };

    const applyFilters = (geoFilters) => {
        const newFilters = { ...filters, ...geoFilters };
        setFilters(newFilters);
        updateUrlParams(newFilters);
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        updateUrlParams(newFilters);
    };

    const updateUrlParams = (f) => {
        const params = new URLSearchParams();
        if (f.city) params.set('city', f.city);
        if (f.department) params.set('department', f.department);
        if (f.region) params.set('region', f.region);
        if (f.specialization) params.set('specialization', f.specialization);
        if (f.topPerformersOnly) params.set('top', 'true');
        setSearchParams(params);
    };

    const handleSearchSubmit = () => {
        setShowSuggestions(false);
        if (searchInput) {
            applyFilters({ city: searchInput, department: '', region: '' });
        }
    };

    const clearFilters = () => {
        setFilters({ city: '', department: '', region: '', specialization: '', topPerformersOnly: false });
        setSearchInput('');
        setSuggestions([]);
        setSearchParams({});
    };

    const hasFilters = filters.city || filters.department || filters.region || filters.specialization || filters.topPerformersOnly;
    const totalCount = agents.length + nearbyAgents.length;
    const topPerformers = agents.filter(a => a.is_top_performer);
    const regularAgents = agents.filter(a => !a.is_top_performer);

    return (
        <div className="min-h-screen pt-24 pb-16" data-testid="agents-page">
            <div className="container-scalimmo">
                {/* Header */}
                <div className="mb-10">
                    <h1 className="font-heading text-4xl md:text-5xl font-semibold text-scalimmo-navy mb-4">
                        Trouvez votre agent
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        {totalCount} agents disponibles
                        {geoInfo && filters.city && (
                            <span> autour de <strong>{geoInfo.city_name}</strong> ({geoInfo.department_name})</span>
                        )}
                        {filters.department && geoInfo && (
                            <span> dans le département <strong>{geoInfo?.department_name || filters.department}</strong></span>
                        )}
                    </p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-border/60 p-4 md:p-6 mb-6 shadow-soft">
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                        {/* Location Search with Autocomplete */}
                        <div className="flex-1 w-full relative" ref={suggestionsRef}>
                            <Label className="text-sm text-muted-foreground mb-2 block">Ville, département ou région</Label>
                            <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Ex: Lyon, Vénissieux, Rhône..."
                                    value={searchInput}
                                    onChange={(e) => handleInputChange(e.target.value)}
                                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                                    className="pl-10"
                                    data-testid="filter-location"
                                />
                            </div>

                            {/* Autocomplete Dropdown */}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-border/60 shadow-lg max-h-64 overflow-y-auto" data-testid="geo-suggestions">
                                    {suggestions.map((s, idx) => (
                                        <button
                                            key={`${s.type}-${s.name}-${idx}`}
                                            onClick={() => selectSuggestion(s)}
                                            className="w-full px-4 py-2.5 text-left hover:bg-muted/40 transition-colors flex items-center gap-3 border-b border-border/20 last:border-0"
                                        >
                                            {s.type === 'city' ? (
                                                <MapPin className="w-4 h-4 text-scalimmo-navy shrink-0" />
                                            ) : (
                                                <Building className="w-4 h-4 text-blue-600 shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-scalimmo-navy">{s.name}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {s.type === 'city'
                                                        ? `${s.department_name} (${s.department_code}) - ${s.region_name}`
                                                        : `Département ${s.department_code}`
                                                    }
                                                </p>
                                            </div>
                                            <Badge variant="outline" className="text-[10px] shrink-0">
                                                {s.type === 'city' ? 'Ville' : 'Dept.'}
                                            </Badge>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Specialization */}
                        <div className="w-full md:w-48">
                            <Label className="text-sm text-muted-foreground mb-2 block">Spécialité</Label>
                            <Select
                                value={filters.specialization}
                                onValueChange={(value) => handleFilterChange('specialization', value === 'all' ? '' : value)}
                            >
                                <SelectTrigger data-testid="filter-specialization">
                                    <SelectValue placeholder="Toutes" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes</SelectItem>
                                    {specializations.map((spec) => (
                                        <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Top Performers Toggle */}
                        <div className="flex items-center gap-3 py-2">
                            <Switch
                                id="top-performers"
                                checked={filters.topPerformersOnly}
                                onCheckedChange={(checked) => handleFilterChange('topPerformersOnly', checked)}
                                data-testid="filter-top-performers"
                            />
                            <Label htmlFor="top-performers" className="flex items-center gap-1.5 text-sm cursor-pointer">
                                <Award className="w-4 h-4 text-scalimmo-gold" />
                                Top Performers
                            </Label>
                        </div>

                        <Button className="btn-primary gap-2" onClick={handleSearchSubmit} data-testid="search-btn">
                            <Search className="w-4 h-4" /> Rechercher
                        </Button>

                        {hasFilters && (
                            <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground" data-testid="clear-filters">
                                Effacer
                            </Button>
                        )}
                    </div>
                </div>

                {/* Geo Expansion Suggestions */}
                {geoInfo && filters.city && (agents.length < 5) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="geo-expand-banner">
                        <Map className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm text-blue-800">
                                Peu de résultats pour <strong>{geoInfo.city_name}</strong> ?
                                Élargissez votre recherche :
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="bg-white text-blue-700 border-blue-300 hover:bg-blue-100 gap-1" onClick={searchByDepartment}>
                                <Building className="w-3.5 h-3.5" />
                                {geoInfo.department_name} ({geoInfo.department_code})
                            </Button>
                            <Button variant="outline" size="sm" className="bg-white text-blue-700 border-blue-300 hover:bg-blue-100 gap-1" onClick={searchByRegion}>
                                <Map className="w-3.5 h-3.5" />
                                {geoInfo.region_name}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" />
                    </div>
                ) : totalCount === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="font-heading text-xl font-semibold text-scalimmo-navy mb-2">
                            Aucun agent trouvé
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            Essayez de modifier vos critères de recherche
                        </p>
                        {geoInfo && (
                            <div className="flex justify-center gap-3">
                                <Button variant="outline" onClick={searchByDepartment} className="gap-1">
                                    <Building className="w-4 h-4" />
                                    Chercher dans {geoInfo.department_name}
                                </Button>
                                <Button variant="outline" onClick={searchByRegion} className="gap-1">
                                    <Map className="w-4 h-4" />
                                    Chercher dans {geoInfo.region_name}
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Top Performers Section */}
                        {topPerformers.length > 0 && !filters.topPerformersOnly && (
                            <div className="mb-12">
                                <div className="flex items-center gap-2 mb-6">
                                    <Award className="w-5 h-5 text-scalimmo-gold" />
                                    <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy">
                                        Top Performers
                                    </h2>
                                    <span className="text-sm text-muted-foreground">({topPerformers.length})</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {topPerformers.map((agent) => (
                                        <AgentCard key={agent.id} agent={agent} variant="elite" />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Regular Agents */}
                        {regularAgents.length > 0 && !filters.topPerformersOnly && (
                            <div className="mb-12">
                                <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy mb-6">
                                    Agents
                                    <span className="text-sm font-normal text-muted-foreground ml-2">({regularAgents.length})</span>
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {regularAgents.map((agent) => (
                                        <AgentCard key={agent.id} agent={agent} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Top performers only mode */}
                        {filters.topPerformersOnly && agents.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                                {agents.map((agent) => (
                                    <AgentCard key={agent.id} agent={agent} variant="elite" />
                                ))}
                            </div>
                        )}

                        {/* Nearby Agents (department-level) */}
                        {nearbyAgents.length > 0 && (
                            <div data-testid="nearby-agents-section">
                                <div className="flex items-center gap-2 mb-6">
                                    <Map className="w-5 h-5 text-blue-600" />
                                    <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy">
                                        Agents à proximité
                                    </h2>
                                    <span className="text-sm text-muted-foreground">
                                        ({nearbyAgents.length} dans le département {geoInfo?.department_name || ''})
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {nearbyAgents.map((agent) => (
                                        <AgentCard key={agent.id} agent={agent} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AgentsPage;
